import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useFeesListScreen } from './hooks/useFeesListScreen'
import { useExportJob } from '../../../hooks/useExportJob'
import AppPagination from '../../../components/AppPagination'
import AppActionMenu from '../../../components/AppActionMenu'
import SharedFilters from '../../../components/SharedFilters'
import { appToast } from '../../../components/AppToast'

function formatCurrency(value) {
  return `₹${Number(value || 0).toLocaleString('en-IN')}`
}

function formatDate(value) {
  if (!value) return ''
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

function getInitials(name) {
  return String(name || '?')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

function getStatusBadge(row) {
  const status = row.status || (
    Number(row.balance || 0) <= 0 && Number(row.totalFees || 0) > 0
      ? 'Paid'
      : Number(row.overdue || 0) > 0
      ? 'OverDue'
      : Number(row.paidAmount || 0) > 0
      ? 'Partial'
      : 'Balance'
  )

  switch (status.toLowerCase()) {
    case 'paid':
      return (
        <span className="inline-flex items-center rounded-md border border-emerald-300/60 bg-emerald-50 px-2 py-0.5 text-[11px] font-extrabold text-emerald-800 shadow-2xs">
          Paid
        </span>
      )
    case 'partial':
      return (
        <span className="inline-flex items-center rounded-md border border-blue-300/60 bg-blue-50 px-2 py-0.5 text-[11px] font-extrabold text-blue-800 shadow-2xs">
          Partial
        </span>
      )
    case 'overdue':
      return (
        <span className="inline-flex items-center rounded-md border border-amber-300/60 bg-amber-50 px-2 py-0.5 text-[11px] font-extrabold text-amber-900 shadow-2xs">
          OverDue
        </span>
      )
    default:
      return (
        <span className="inline-flex items-center rounded-md border border-purple-300/60 bg-purple-50 px-2 py-0.5 text-[11px] font-extrabold text-purple-800 shadow-2xs">
          Balance
        </span>
      )
  }
}

export default function FeesListScreen() {
  const navigate = useNavigate()
  const { state, actions } = useFeesListScreen()

  const { triggerExport, isExporting } = useExportJob({ module: 'fees' })

  const handleExport = () => {
    triggerExport({
      searchField: state.searchField,
      searchText: state.searchText,
      company: state.companyFilter === 'All' ? '' : state.companyFilter,
      branch: state.branchFilter === 'All' ? '' : state.branchFilter,
      course: state.courseFilter === 'All' ? '' : state.courseFilter,
      scheme: state.schemeFilter === 'All' ? '' : state.schemeFilter,
      batch: state.batchFilter === 'All' ? '' : state.batchFilter,
      gender: state.genderFilter === 'All' ? '' : state.genderFilter,
      fromDate: state.fromDate,
      toDate: state.toDate,
      studentId: state.studentId,
    })
  }

  const handleOpenBulkSms = () => {
    if (state.rows.length === 0) {
      appToast.warning('No student records available to send SMS.')
      return
    }
    navigate('/admission/fees/send-sms')
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* ── Page Header ─────────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Admission Module</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Fees Management
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Search, filter, track student fee collections, installments, and receipts.
          </p>
        </div>

        {state.fromStudent && (
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admission/students/list')}
              className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2.5 text-xs font-bold text-on-surface-variant transition-colors hover:bg-surface-container-high cursor-pointer"
            >
              <span className="material-symbols-outlined text-base">arrow_back</span>
              Back to Students
            </button>
          </div>
        )}
      </section>

      {/* ── KPI Summary Cards (Interactive Filters) ────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {/* Total Fees */}
        <div
          onClick={() => actions.handleKpiCardClick('All')}
          className={`flex items-center gap-3.5 rounded-2xl border p-4 shadow-xs transition cursor-pointer hover:shadow-md ${
            state.typeFilter === 'All'
              ? 'border-blue-500 bg-blue-50/40 ring-2 ring-blue-500/20'
              : 'border-outline-variant/25 bg-surface-container-lowest'
          }`}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <span className="material-symbols-outlined text-xl">account_balance_wallet</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Total Fees</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-on-surface tabular-nums">
              {formatCurrency(state.summary.total)}
            </p>
          </div>
        </div>

        {/* Paid */}
        <div
          onClick={() => actions.handleKpiCardClick('Paid')}
          className={`flex items-center gap-3.5 rounded-2xl border p-4 shadow-xs transition cursor-pointer hover:shadow-md ${
            state.typeFilter === 'Paid'
              ? 'border-emerald-500 bg-emerald-50/40 ring-2 ring-emerald-500/20'
              : 'border-outline-variant/25 bg-surface-container-lowest'
          }`}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <span className="material-symbols-outlined text-xl">check_circle</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Paid (Receipts)</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-emerald-700 tabular-nums">
              {formatCurrency(state.summary.paid)}
            </p>
          </div>
        </div>

        {/* Balance */}
        <div
          onClick={() => actions.handleKpiCardClick('Balance')}
          className={`flex items-center gap-3.5 rounded-2xl border p-4 shadow-xs transition cursor-pointer hover:shadow-md ${
            state.typeFilter === 'Balance'
              ? 'border-purple-500 bg-purple-50/40 ring-2 ring-purple-500/20'
              : 'border-outline-variant/25 bg-surface-container-lowest'
          }`}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
            <span className="material-symbols-outlined text-xl">pending</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Balance</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-purple-700 tabular-nums">
              {formatCurrency(state.summary.balance)}
            </p>
          </div>
        </div>

        {/* Overdue / Due */}
        <div
          onClick={() => actions.handleKpiCardClick('OverDue')}
          className={`flex items-center gap-3.5 rounded-2xl border p-4 shadow-xs transition cursor-pointer hover:shadow-md ${
            state.typeFilter === 'OverDue'
              ? 'border-amber-500 bg-amber-50/40 ring-2 ring-amber-500/20'
              : 'border-outline-variant/25 bg-surface-container-lowest'
          }`}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <span className="material-symbols-outlined text-xl">warning</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Due / Overdue</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-amber-700 tabular-nums">
              {formatCurrency(state.summary.overdue)}
            </p>
          </div>
        </div>
      </div>

      {/* ── Shared Filters ─────────────────────────────────────────────────── */}
      <SharedFilters
        searchField={state.searchField}
        setSearchField={actions.setSearchField}
        searchText={state.searchText}
        setSearchText={actions.setSearchText}
        searchFieldOptions={state.options.searchFieldOptions}
        companyFilter={state.companyFilter}
        setCompanyFilter={actions.setCompanyFilter}
        companyOptions={state.options.companyOptions}
        branchFilter={state.branchFilter}
        setBranchFilter={actions.setBranchFilter}
        branchOptions={state.options.branchOptions}
        courseFilter={state.courseFilter}
        setCourseFilter={actions.setCourseFilter}
        courseOptions={state.options.courseOptions}
        schemeFilter={state.schemeFilter}
        setSchemeFilter={actions.setSchemeFilter}
        schemeOptions={state.options.schemeOptions}
        batchFilter={state.batchFilter}
        setBatchFilter={actions.setBatchFilter}
        batchOptions={state.options.batchOptions}
        academicYearFilter={state.academicYearFilter}
        setAcademicYearFilter={actions.setAcademicYearFilter}
        academicYearOptions={state.options.academicYearOptions}
        datePeriodFilter={state.datePeriodFilter}
        setDatePeriodFilter={actions.handlePeriodChange}
        datePeriodOptions={state.options.datePeriodOptions}
        fromDate={state.fromDate}
        setFromDate={actions.setFromDate}
        toDate={state.toDate}
        setToDate={actions.setToDate}
        subjectFilter={state.subjectFilter}
        setSubjectFilter={actions.setSubjectFilter}
        subjectOptions={state.options.subjectOptions}
        genderFilter={state.genderFilter}
        setGenderFilter={actions.setGenderFilter}
        genderOptions={state.options.genderOptions}
        statusFilter={state.typeFilter}
        setStatusFilter={actions.setTypeFilter}
        statusOptions={state.options.typeOptions}
        onSearch={actions.refresh}
        onReset={actions.resetFilters}
        visibleAccordionFilters={[
          'company',
          'branch',
          'course',
          'scheme',
          'academicYear',
          'datePeriod',
          'fromDate',
          'toDate',
          'batch',
          'subject',
          'gender',
        ]}
        tools={[
          { key: 'upload', label: 'Upload Excel', icon: 'cloud_upload' },
          { key: 'sms', label: 'Send SMS', icon: 'sms' },
          { key: 'export', label: 'Export Excel', icon: 'description' },
          { key: 'print', label: 'Print List', icon: 'print' },
        ]}
        onToolSelect={(key) => {
          if (key === 'upload') navigate('/admission/upload-excel/list')
          else if (key === 'export') handleExport()
          else if (key === 'print') window.print()
          else if (key === 'sms') handleOpenBulkSms()
        }}
      />

      {/* ── Data Table Card ────────────────────────────────────────────────── */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-outline-variant/15 bg-surface-container-low/40 px-6 py-3">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-base text-primary">payments</span>
            <span className="text-xs font-bold uppercase tracking-wider text-on-surface">
              List of Student Fees
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-on-surface-variant">
              {state.rows.length} {state.rows.length === 1 ? 'Record' : 'Records'}
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-outline-variant/15 bg-surface-container-low/50 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
              <tr>
                <th className="px-5 py-3.5">Student</th>
                <th className="px-5 py-3.5">Company &amp; Branch</th>
                <th className="px-5 py-3.5">Course &amp; Batch</th>
                <th className="px-5 py-3.5 text-right">Fees / Paid</th>
                <th className="px-5 py-3.5 text-right">Balance / Due</th>
                <th className="px-5 py-3.5 text-center">Status</th>
                <th className="w-24 px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {state.isLoading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-sm text-on-surface-variant font-medium">
                    <span className="material-symbols-outlined animate-spin text-3xl text-primary/40 block mb-2">
                      progress_activity
                    </span>
                    Loading fees data...
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <span className="material-symbols-outlined mb-2 block text-4xl text-on-surface-variant/40">
                      payments
                    </span>
                    <p className="text-sm font-medium text-on-surface-variant">
                      No fees records found matching current criteria.
                    </p>
                    <button
                      type="button"
                      onClick={actions.resetFilters}
                      className="mt-3 inline-flex items-center gap-1 rounded-xl bg-primary/10 px-4 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition cursor-pointer"
                    >
                      Reset Filters
                    </button>
                  </td>
                </tr>
              ) : (
                state.paginatedRows.map((row) => (
                  <tr
                    key={row.id}
                    className="transition-colors hover:bg-surface-container-low/40 cursor-pointer"
                    onClick={() => navigate(`/admission/fees/entry?id=${row.id}`)}
                  >
                    {/* Student */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-xs font-black text-primary">
                          {getInitials(row.studentName)}
                        </div>
                        <div>
                          <p className="font-bold text-on-surface">{row.studentName}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="inline-flex items-center rounded-md bg-surface-container-high px-1.5 py-0.2 text-[10px] font-bold text-on-surface-variant">
                              Roll #{row.rollNo}
                            </span>
                            {row.joiningDate && (
                              <span className="text-[11px] text-on-surface-variant/70">
                                {formatDate(row.joiningDate)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Company & Branch */}
                    <td className="px-5 py-3.5">
                      <p className="font-semibold text-on-surface">{row.company || '—'}</p>
                      <p className="text-xs text-on-surface-variant">{row.branch || '—'}</p>
                    </td>

                    {/* Course & Batch */}
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center rounded-lg bg-surface-container-low px-2.5 py-1 text-xs font-semibold text-secondary">
                        {row.course || '—'}
                      </span>
                      {row.batch && (
                        <p className="mt-1 text-[11px] text-on-surface-variant">{row.batch}</p>
                      )}
                    </td>

                    {/* Fees / Paid */}
                    <td className="px-5 py-3.5 text-right">
                      <p className="text-sm font-bold text-on-surface tabular-nums">
                        {formatCurrency(row.totalFees)}
                      </p>
                      <p className="text-xs font-bold text-emerald-600 tabular-nums">
                        {formatCurrency(row.paidAmount)}
                      </p>
                    </td>

                    {/* Balance / Due */}
                    <td className="px-5 py-3.5 text-right">
                      <p className="text-sm font-bold text-purple-700 tabular-nums">
                        {formatCurrency(row.balance)}
                      </p>
                      {Number(row.overdue || 0) > 0 ? (
                        <p className="text-xs font-bold text-amber-700 tabular-nums">
                          {formatCurrency(row.overdue)}
                        </p>
                      ) : (
                        <p className="text-xs text-on-surface-variant/60">—</p>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-5 py-3.5 text-center">{getStatusBadge(row)}</td>

                    {/* Actions Menu */}
                    <td className="px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <AppActionMenu
                        triggerLabel=""
                        triggerIcon="more_vert"
                        showChevron={false}
                        triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                        items={[
                          { key: 'edit', label: 'Edit Student Admission', icon: 'edit' },
                          { key: 'details', label: 'View Fee Structure', icon: 'visibility' },
                          { key: 'sms', label: 'Send SMS', icon: 'sms' },
                          { key: 'ledger', label: 'Student Ledger', icon: 'account_balance_wallet' },
                          { key: 'receipt', label: 'Add Receipt', icon: 'receipt_long' },
                        ]}
                        onSelect={(key) => {
                          const targetStudentId = row.studentId || row.id
                          if (key === 'edit') navigate(`/admission/students/entry?id=${targetStudentId}&mode=edit`)
                          else if (key === 'details') navigate(`/admission/fees/entry?id=${targetStudentId}`)
                          else if (key === 'sms') navigate(`/admission/fees/send-sms?studentId=${targetStudentId}`)
                          else if (key === 'ledger') navigate(`/admission/students/ledger?studentId=${targetStudentId}`)
                          else if (key === 'receipt') navigate(`/admission/receipts/entry?studentId=${targetStudentId}`)
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Table Footer & Shared Pagination ─────────────────────────────── */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing Page <span className="font-bold text-on-surface">{state.currentPage}</span> of{' '}
            <span className="font-bold text-on-surface">{state.totalPages}</span> ({state.rows.length} total items)
          </p>

          <AppPagination
            page={state.currentPage}
            totalPages={state.totalPages}
            onPageChange={actions.setCurrentPage}
            onPrevious={() => actions.setCurrentPage((p) => Math.max(1, p - 1))}
            onNext={() => actions.setCurrentPage((p) => Math.min(state.totalPages, p + 1))}
            canPrevious={state.currentPage > 1}
            canNext={state.currentPage < state.totalPages}
          />
        </div>
      </section>
    </div>
  )
}
